import {useCallback, useEffect, useState} from 'react';

import {API} from "../api";

/** Fired by the pages that empty a queue, so the sidebar badge follows along */
export const PENDING_REQUESTS_EVENT = 'admin:pending-requests-changed';

export const notifyPendingRequestsChanged = () =>
    window.dispatchEvent(new Event(PENDING_REQUESTS_EVENT));

const POLL_INTERVAL_MS = 60_000;

/**
 * What is waiting for the admin's attention, keyed by the id of the menu item
 * that shows it: submissions still to be moderated, and reviews nobody has
 * read yet. Polled slowly and refreshed on demand — the numbers only change
 * when a user writes in or an admin works through the queue.
 */
export default function usePendingRequestCounts(intervalMs = POLL_INTERVAL_MS) {
    const [counts, setCounts] = useState({
        'service-requests': 0,
        'construction-requests': 0,
        'master-requests': 0,
        review: 0,
    });

    const load = useCallback(async () => {
        try {
            // Each queue is moderated on its own page, so each badge counts
            // only its own section
            const [services, construction, masters, reviews] = await Promise.all([
                API.companyRequestsCount('auto'),
                API.companyRequestsCount('construction'),
                API.masterRequestsCount(),
                API.reviewCount(),
            ]);

            setCounts({
                'service-requests': Number(services.data) || 0,
                'construction-requests': Number(construction.data) || 0,
                'master-requests': Number(masters.data) || 0,
                review: Number(reviews.data) || 0,
            });
        } catch (e) {
            // A badge is never worth interrupting the admin with an error
        }
    }, []);

    useEffect(() => {
        load();

        const timer = setInterval(load, intervalMs);
        window.addEventListener(PENDING_REQUESTS_EVENT, load);

        return () => {
            clearInterval(timer);
            window.removeEventListener(PENDING_REQUESTS_EVENT, load);
        };
    }, [load, intervalMs]);

    return counts;
}
